<!--
	@component
	Renders the sorted list of a dropdown property's list elements (its "values"). Each row
	shows the element name, an optional tooltip when it has child properties, and hover-revealed
	priority / add-child / delete controls. Extracted from MaterialPropertyForm; the parent owns
	the data and the actions, this component only renders and delegates via callbacks.
-->
<script lang="ts">
	import type { MaterialPropertyListElement } from './types/materialProperty'
	import { t } from '$i18n/i18n'
	import Tooltip from '#lib/atoms/Tooltip.svelte'
	import { TooltipPlacements } from '$types/enums'
	import { mmpvState } from './mmpv.state.svelte'
	import addIcon from '@/icons/icon_add_green.svg'
	import { escapeHtml } from '$utils/utils.svelte'

	interface Props {
		propertyListElements: MaterialPropertyListElement[]
		// open the given list element (navigates to it)
		onSelect: (listElement: MaterialPropertyListElement) => void
		// add a new child property to the given list element
		onAddChild: (listElement: MaterialPropertyListElement) => void
		// remove the given list element from the property
		onRemove: (listElement: MaterialPropertyListElement) => void
	}

	let { propertyListElements, onSelect, onAddChild, onRemove }: Props = $props()

	let sortedListElements: MaterialPropertyListElement[] = $derived(
		[...propertyListElements].sort(
			(a: MaterialPropertyListElement, b: MaterialPropertyListElement) =>
				(a?.priority ?? Number.MAX_SAFE_INTEGER) - (b?.priority ?? Number.MAX_SAFE_INTEGER)
		)
	)

	function propertyName(propertyId: string | undefined): string {
		return escapeHtml(propertyId ? mmpvState.getMaterialProperty(propertyId)?.name : undefined)
	}

	/**
	 * Constructs tooltip content for a list element that contains properties.
	 * Content is built according to the number of properties in the list element.
	 * The options are: one, two or more properties.
	 * We ignore 0, because we do not show the tooltip anyways
	 * @param {MaterialPropertyListElement} listElement - The list element for which the tooltip content is to be built.
	 * @returns {string} The tooltip content string.
	 */
	function buildTooltipContentForListElementWithProperties(
		listElement: MaterialPropertyListElement
	): string {
		let result
		switch (listElement.propertyChildIds.length) {
			case 1:
				result = $t('UI.mmpv.materialPropertyListElement.addedProperty.one.tt', {
					name: propertyName(listElement.propertyChildIds[0])
				})
				break
			case 2:
				result = $t('UI.mmpv.materialPropertyListElement.addedProperty.two.tt', {
					name: propertyName(listElement.propertyChildIds[0]),
					name2: propertyName(listElement.propertyChildIds[1])
				})
				break
			default:
				result = $t('UI.mmpv.materialPropertyListElement.addedProperty.default.tt', {
					name: propertyName(listElement.propertyChildIds[0]),
					name2: propertyName(listElement.propertyChildIds[1])
				})
		}
		return result
	}
</script>

{#each sortedListElements as listElement (listElement.id)}
	<div class="material-property-list-element">
		<div>
			<span
				role="button"
				tabindex="0"
				onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(listElement)}
				onclick={() => onSelect(listElement)}>{listElement.name}</span
			>
			{#if listElement.propertyChildIds !== undefined && listElement.propertyChildIds.length > 0}
				<Tooltip
					ttipIconHeight="20px"
					ttipIconWidth="20px"
					maxWidthTtip={370}
					placementTtip={TooltipPlacements.RIGHT}
					msg={"<div style='line-height: 20px;'> " +
						buildTooltipContentForListElementWithProperties(listElement) +
						'</div>'}
					isHtml
				/>
			{/if}
		</div>
		<div>
			<div class="material-property-priority">
				<span
					>{$t('UI.mmpv.propertyListElement.priority.info', {
						prio: listElement.priority
					})}</span
				>
			</div>
			<div
				class="element-add-child-btn"
				role="button"
				tabindex="0"
				onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && onAddChild(listElement)}
				onclick={() => onAddChild(listElement)}
			>
				<Tooltip
					placementTtip={TooltipPlacements.RIGHT}
					ttipIconImage={addIcon}
					msg={$t('UI.mmpv.propertyListElement.addProperty.tooltip')}
				/>
			</div>
			<div
				class="element-delete-btn"
				role="button"
				tabindex="0"
				onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && onRemove(listElement)}
				onclick={() => onRemove(listElement)}
			></div>
		</div>
	</div>
{/each}

<style lang="scss">
	@import '$styles/global';

	div.material-property-list-element {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: flex-start;
		gap: 14px;

		width: 100%;
		border-bottom: 1px solid var(--color-gray-100);
		padding: 0.75rem 0;

		& > div {
			width: 100%;

			&:first-child {
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: flex-start;
				gap: 6px;

				font-family: Roboto, sans-serif;
				line-height: 10px;
				font-weight: 400;
				font-size: 0.75rem;
				color: var(--primary-green);
			}

			&:last-child {
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: flex-end;
				gap: 8px;

				margin-right: 0.875rem;
			}

			& > span {
				cursor: pointer;
			}
		}

		& .element-delete-btn {
			background: url('@/icons/icon_trash_red.svg') no-repeat center;
		}

		& .material-property-priority {
			visibility: hidden;

			& > span {
				font-family: Roboto, sans-serif;
				line-height: 10px;
				font-weight: 400;
				font-size: 0.75rem;
				color: var(--color-gray-400);
			}
		}

		& .element-add-child-btn,
		& .element-delete-btn {
			width: 24px;
			height: 24px;
			visibility: hidden;
			cursor: pointer;
		}

		&:hover,
		&:focus-within {
			& .material-property-priority,
			& .element-add-child-btn,
			& .element-delete-btn {
				visibility: visible;
			}
		}
	}
</style>
